import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Info, SlidersHorizontal } from "lucide-react"

import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Toolbar, ToolbarField } from "@/components/Toolbar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth, useRbac } from "@/lib/auth"
import { formatAmount, formatDate, formatPercent } from "@/lib/format"
import { cn } from "@/lib/utils"

import { fetchConciliacion, fetchFacturas } from "@/routes/finance/api"
import { ConsiderarFacturaDialog } from "@/routes/finance/ConsiderarFacturaDialog"
import { useFinanceFilters } from "@/routes/finance/context"
import {
  CONSIDERAR_EN,
  ESTADOS_CONCILIACION,
  cuentaEnFinanzas,
  etiquetaConsiderar,
  nombrePais,
  porQueNoSuma,
} from "@/routes/finance/mapping"
import type { ConciliacionPais, Factura } from "@/routes/finance/mapping"
import { BotonDescargar } from "@/components/BotonDescargar"
import { t } from "@/lib/i18n"

const MESES = ["ene", "feb", "mar", "abr", "may", "jun",
               "jul", "ago", "sep", "oct", "nov", "dic"]

const TONO_BADGE = {
  ok: "success-soft",
  aviso: "warning-soft",
  alerta: "destructive-soft",
  neutro: "outline",
} as const

/**
 * Conciliacion: el modelo de gestion contra lo facturado.
 *
 * UNA DIFERENCIA NO ES UN ERROR. Hay ingreso devengado sin factura emitida y
 * facturas que no son ingreso del periodo; el descalce entre ambos es
 * informacion legitima del negocio. Lo que esta pantalla hace es separar dos
 * cosas que un total consolidado mezcla hasta volverlas indistinguibles:
 *
 *   "no cuadra"      -> se INVESTIGA. Descalce real entre devengado y facturado.
 *   "no esta cargado"-> se IMPORTA.   Faltan facturas; el negocio esta bien.
 *
 * Piden acciones opuestas, y por eso el corte es por PAIS y el estado lo calcula
 * el backend a partir de la cobertura, en vez de dejarlo a la interpretacion de
 * quien mire la resta.
 *
 * La mitad de abajo es lo que hasta hoy solo se podia hacer por SQL: mirar las
 * facturas del periodo que no cuadra y decidir, una por una, en que lado cuenta
 * cada una. Una decision de negocio que solo se toma con un cliente de base de
 * datos no se toma: se posterga, y la conciliacion queda descuadrada para
 * siempre con una explicacion que vive en la cabeza de alguien.
 */
export function ConciliacionPage() {
  const { activeTenantId } = useAuth()
  const { can } = useRbac()
  const { anio, pais, paisQuery } = useFinanceFilters()

  const [mes, setMes] = React.useState<string>("")
  const [considerar, setConsiderar] = React.useState<string>("")
  const [buscar, setBuscar] = React.useState("")
  const [factura, setFactura] = React.useState<Factura | null>(null)

  const puedeEditar = can("edit", activeTenantId ?? undefined)

  const conc = useQuery({
    queryKey: ["finance-conciliacion", activeTenantId, anio],
    queryFn: () => fetchConciliacion(anio),
    enabled: Boolean(activeTenantId),
  })

  const facturas = useQuery({
    queryKey: ["finance-facturas", activeTenantId, anio, pais, mes, considerar, buscar],
    queryFn: () => fetchFacturas({
      anio,
      mes: mes ? Number(mes) : undefined,
      pais: paisQuery,
      considerar_en: considerar || undefined,
      buscar,
    }),
    enabled: Boolean(activeTenantId),
  })

  // El filtro de pais del layout manda tambien aca: si hay uno elegido, la
  // conciliacion muestra ese pais y no los cuatro. Lo contrario obligaria a
  // mantener dos filtros de pais en la misma pantalla.
  const paises: ConciliacionPais[] = React.useMemo(() => {
    const todos = conc.data?.paises ?? []
    return pais ? todos.filter((p) => p.pais === pais) : todos
  }, [conc.data, pais])

  const totalIngreso = paises.reduce((a, p) => a + p.ingreso, 0)
  const totalFacturado = paises.reduce((a, p) => a + p.facturado, 0)
  const conDescalce = paises.filter((p) => p.estado === "diferencia").length

  return (
    <div className="space-y-4">
      <MetricRow className="lg:grid-cols-3">
        <MetricCard label={`Ventas reconocidas · ${anio}`} value={totalIngreso}
                    loading={conc.isLoading} />
        <MetricCard label={t("Facturado")} value={totalFacturado} loading={conc.isLoading} />
        <MetricCard
          label={t("Países con descalce real")}
          value={null}
          loading={conc.isLoading}
          tono={conDescalce > 0 ? "alerta" : "neutro"}
          badge={
            conDescalce > 0
              ? `${conDescalce} de ${paises.length}`
              : paises.length
                ? "ninguno"
                : "sin datos"
          }
        />
      </MetricRow>

      {/* --------------------------------------------- resumen por pais --- */}
      <WorkPanel
        title={t("Finanzas contra facturación")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {t("Por país, porque «no cuadra» y «no está cargado» piden acciones opuestas")}</span>
        }
        footnote={conc.data?.nota}
      >
        <DataState
          loading={conc.isLoading}
          error={conc.error}
          empty={!conc.isLoading && paises.length === 0}
          emptyMessage={t("No hay ingreso ni facturas en este año.")}
          emptyHint={t("Elige otro año en la barra de arriba, o importa las facturas del período.")}
          onRetry={() => void conc.refetch()}
          columns={[16, 14, 14, 14, 10, 32]}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("País")}</TableHead>
                <TableHead className="text-right">{t("Ventas")}</TableHead>
                <TableHead className="text-right">{t("Facturado")}</TableHead>
                <TableHead className="text-right">{t("Diferencia")}</TableHead>
                <TableHead className="text-right">{t("Cobertura")}</TableHead>
                <TableHead>{t("Cómo se lee")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paises.map((p) => {
                const e = ESTADOS_CONCILIACION[p.estado]
                return (
                  <React.Fragment key={p.pais}>
                    <TableRow>
                      <TableCell className="font-medium">
                        {nombrePais(p.pais)}
                        <span className="ml-1.5 text-2xs text-muted-foreground">
                          {p.facturas} factura{p.facturas === 1 ? "" : "s"}
                        </span>
                      </TableCell>
                      <TableCell className="num text-right">{formatAmount(p.ingreso)}</TableCell>
                      <TableCell className="num text-right">{formatAmount(p.facturado)}</TableCell>
                      <TableCell
                        className={cn(
                          "num text-right",
                          p.estado === "diferencia" && "font-medium text-destructive",
                        )}
                      >
                        {formatAmount(p.diferencia)}
                      </TableCell>
                      <TableCell className="num text-right">
                        {p.cobertura == null ? "—" : formatPercent(p.cobertura, 0)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={TONO_BADGE[e.tono]}>{e.label}</Badge>
                        <p className="mt-0.5 max-w-[52ch] text-2xs leading-4 text-muted-foreground">
                          {p.nota}
                        </p>
                      </TableCell>
                    </TableRow>

                    {/* El detalle mensual solo cuando hay algo que mirar: en un
                        pais que cuadra, doce filas mas son ruido. */}
                    {p.estado === "diferencia" ? (
                      <TableRow className="bg-surface-sunken/40">
                        <TableCell colSpan={6} className="py-1.5">
                          <div className="flex flex-wrap gap-1">
                            {p.meses
                              .filter((m) => Math.abs(m.diferencia) >= 1)
                              .map((m) => (
                                <button
                                  key={m.mes}
                                  type="button"
                                  onClick={() => setMes(String(m.mes))}
                                  className={cn(
                                    "rounded border px-1.5 py-0.5 text-2xs transition-colors",
                                    "hover:border-border-strong hover:bg-surface",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                    mes === String(m.mes) && "border-brand/50 bg-brand-soft",
                                  )}
                                  title={t("Ver las facturas de este mes")}
                                >
                                  <span className="font-medium">{MESES[m.mes - 1]}</span>{" "}
                                  <span className="num text-muted-foreground">
                                    {formatAmount(m.diferencia)}
                                  </span>
                                </button>
                              ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      {/* ------------------------------------------------------ facturas --- */}
      <Toolbar>
        <ToolbarField label={t("Mes")} htmlFor="mes-facturas">
          <Select value={mes || "TODOS"} onValueChange={(v) => setMes(v === "TODOS" ? "" : v)}>
            <SelectTrigger id="mes-facturas" className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">{t("Todo el año")}</SelectItem>
              {MESES.map((m, i) => (
                <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Cuenta en")} htmlFor="considerar-facturas">
          <Select
            value={considerar || "TODOS"}
            onValueChange={(v) => setConsiderar(v === "TODOS" ? "" : v)}
          >
            <SelectTrigger id="considerar-facturas" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">{t("Cualquiera")}</SelectItem>
              {CONSIDERAR_EN.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ToolbarField>

        <ToolbarField label={t("Buscar")} htmlFor="buscar-facturas" className="min-w-[16rem] flex-1">
          <Input
            id="buscar-facturas"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder={t("Cliente, número o concepto")}
          />
        </ToolbarField>

        {mes || considerar || buscar ? (
          <Button
            size="xs" variant="outline"
            onClick={() => { setMes(""); setConsiderar(""); setBuscar("") }}
          >
            {t("Quitar filtros")}</Button>
        ) : null}
      </Toolbar>

      <WorkPanel
        title={t("Facturas del período")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {facturas.data
              ? `${facturas.data.facturas.length}${facturas.data.hay_mas ? "+" : ""} en pantalla`
              : "—"}
          </span>
        }
        actions={
          <>
            {puedeEditar ? (
              <span className="flex items-center gap-1 text-2xs text-muted-foreground">
                <SlidersHorizontal className="h-3 w-3" />
                {t("Clic en «cuenta en» para cambiarlo")}</span>
            ) : null}
            <BotonDescargar recurso="facturas" filtros={{ anio, pais: paisQuery }} />
          </>
        }
        footnote={
          <>
            {facturas.data?.nota}
            {facturas.data?.hay_mas ? (
              <>
                {" "}
                <strong>{t("Hay más de las que caben en pantalla")}</strong>{t(": acota por mes, por país o busca por cliente. No se paginan a propósito — una conciliación se mira por período, no recorriendo el año entero.")}</>
            ) : null}
          </>
        }
      >
        <DataState
          loading={facturas.isLoading}
          error={facturas.error}
          empty={!facturas.isLoading && (facturas.data?.facturas.length ?? 0) === 0}
          emptyMessage={t("Ninguna factura con estos filtros.")}
          emptyHint={
            mes || considerar || buscar
              ? "Quita algún filtro: puede que el período no tenga facturas cargadas."
              : "Este año y país no tienen facturas importadas todavía."
          }
          onRetry={() => void facturas.refetch()}
          columns={[8, 10, 24, 20, 10, 12, 16]}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("País")}</TableHead>
                <TableHead>{t("Número")}</TableHead>
                <TableHead>{t("Cliente")}</TableHead>
                <TableHead>{t("Concepto")}</TableHead>
                <TableHead>{t("Emitida")}</TableHead>
                <TableHead className="text-right">{t("Importe USD")}</TableHead>
                <TableHead>{t("Cuenta en")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(facturas.data?.facturas ?? []).map((f) => {
                const suma = cuentaEnFinanzas(f)
                const porque = porQueNoSuma(f)
                return (
                  <TableRow key={f.id} className={cn(!suma && "text-muted-foreground")}>
                    <TableCell>{f.pais}</TableCell>
                    <TableCell className="code text-2xs">{f.numero}</TableCell>
                    <TableCell className="max-w-[24ch] truncate" title={f.cliente}>
                      {f.cliente}
                    </TableCell>
                    <TableCell className="max-w-[28ch] truncate text-2xs"
                               title={f.concepto ?? ""}>
                      {f.concepto || "—"}
                    </TableCell>
                    <TableCell className="text-2xs">
                      {formatDate(f.fecha_emision) || "—"}
                    </TableCell>
                    <TableCell
                      className={cn("num text-right", !suma && "line-through opacity-70")}
                    >
                      {formatAmount(f.importe_usd)}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        disabled={!puedeEditar}
                        onClick={() => setFactura(f)}
                        className={cn(
                          "rounded text-left",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          puedeEditar && "hover:underline",
                        )}
                      >
                        <Badge variant={suma ? "outline" : "warning-soft"}>
                          {f.estado === "ANULADA"
                            ? "Anulada"
                            : etiquetaConsiderar(f.considerar_en)}
                        </Badge>
                      </button>
                      {porque ? (
                        <p className="mt-0.5 max-w-[30ch] text-2xs leading-4 text-muted-foreground">
                          {porque}
                        </p>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      {!puedeEditar ? (
        <p className="flex items-center gap-1.5 text-2xs text-muted-foreground">
          <Info className="h-3 w-3" />
          {t("Tu rol puede leer la conciliación pero no cambiar dónde cuenta una factura.")}</p>
      ) : null}

      <ConsiderarFacturaDialog
        factura={factura}
        open={factura !== null}
        onOpenChange={(v) => { if (!v) setFactura(null) }}
      />
    </div>
  )
}
